//Core imports
import { useCallback, useEffect, useState } from "react";

//Pixel imports
import {
  Button,
  Checklist,
  ChecklistDesc,
  ChecklistItem,
  ChecklistStatus,
  ChecklistTitle,
  Description,
  Label,
  Loader,
  Message,
  Modal,
  PageTitle,
  Panel,
  PanelCard,
  SandboxSvg,
  Text,
  ToggleSwitch,
} from "pixel";

//Helpers
import {
  AutoSetupResponse,
  BatchConfigurationObject,
  Configurations,
  FetchKycCheckConfigurationResponse,
  GetPaymentsGetwayResponse,
  KycRequestConfigurationResponse,
  OmsTenantSettingsResponse,
  OrderConfigurationsResponse,
} from "@fintechprimitives/fpapi";
import { useFpapi } from "../../../../context/fpapi/useFpapi";
import { reportErrorToSentry } from "../../../../global/sentry/sentry";
import { ConditionalRenderer } from "../../../../utils/helpers";
import { isAuthorisedRole, isEmptyObject } from "../../../../utils/utils";
import { useUserInfo } from "../../../../context/userinfo/useUserInfo";
import { FOLIO_MIGRATION_SWITCH_AUTHORISED_ROLES } from "../../../../utils/constants";
import { getKycConfigurationType } from "../KycService/NewKycRequestService/utils";
import appConfig from "../../../../config/appConfig";

type CheckListStatuses = "CONFIGURED" | "NOT_CONFIGURED" | "ERROR";

type ConfigurationStatusService =
  | "ONBOARDING"
  | "FILES"
  | "ACCOUNTS_MF"
  | "BACKPRESSURE"
  | "KYC_GATEWAY"
  | "KYC_REQUEST"
  | "VERIFICATION"
  | "OMS_SEED"
  | "OMS_RTA"
  | "OMS_AMC"
  | "PG_SEED"
  | "PG_RAZORPAY_GATEWAY"
  | "PG_BILLDESK_GATEWAY"
  | "PG_AMC"
  | "BI_TOOLS"
  | "ORDER_SETTINGS"
  | "PG_ONDC_GATEWAY";

type CheckListStatus = Record<ConfigurationStatusService, CheckListStatuses>;

const isAllRequiredServicesConfigured = (
  checkListStatus: CheckListStatus,
  requiredStatuses: Partial<ConfigurationStatusService>[]
) => {
  let allRequiredServicesConfigured = true;
  for (let status of requiredStatuses) {
    if (checkListStatus[status] !== "CONFIGURED") {
      allRequiredServicesConfigured = false;
      break;
    }
  }

  return allRequiredServicesConfigured;
};

const isAnyErrorInServices = (
  checkListStatus: CheckListStatus,
  requiredStatuses: Partial<ConfigurationStatusService>[]
) => {
  let hasError = false;
  for (const status of requiredStatuses) {
    if (checkListStatus[status] === "ERROR") {
      hasError = true;
      break;
    }
  }

  return hasError;
};

function getOMSConfiguredStatus(
  checkListStatus: CheckListStatus,
  orderSettings?: OrderConfigurationsResponse
) {
  let requiredStatuses: Partial<ConfigurationStatusService>[] = [
    "OMS_SEED",
    "ONBOARDING",
    "OMS_RTA",
    "ACCOUNTS_MF",
    "BACKPRESSURE",
    "FILES",
  ];

  const defaultOrderGateway = appConfig.environmentGroup === "ncdex" ? "ncdex" : "ondc";

  if (orderSettings?.default_order_gateway === defaultOrderGateway) {
    requiredStatuses = requiredStatuses.filter((status) => status !== "OMS_RTA");
  }

  const allRequiredServicesConfigured = isAllRequiredServicesConfigured(
    checkListStatus,
    requiredStatuses
  );
  const hasError = isAnyErrorInServices(checkListStatus, requiredStatuses);

  if (allRequiredServicesConfigured) {
    return "success";
  } else if (hasError) {
    return "error";
  } else {
    return "";
  }
}

function getPGConfiguredStatus(checkListStatus: CheckListStatus) {
  const requiredRazorpayStatuses: Partial<ConfigurationStatusService>[] = [
    "PG_SEED",
    "PG_RAZORPAY_GATEWAY",
  ];
  const requiredBilldeskStatuses: Partial<ConfigurationStatusService>[] = [
    "PG_SEED",
    "PG_BILLDESK_GATEWAY",
  ];

  const requiredOndcStatuses: Partial<ConfigurationStatusService>[] = [
    "PG_SEED",
    "PG_ONDC_GATEWAY",
  ];

  const allRequiredServicesConfiguredForRazorpay = isAllRequiredServicesConfigured(
    checkListStatus,
    requiredRazorpayStatuses
  );

  const allRequiredServicesConfiguredForBilldesk = isAllRequiredServicesConfigured(
    checkListStatus,
    requiredBilldeskStatuses
  );

  const allRequiredServicesConfiguredForOndc = isAllRequiredServicesConfigured(
    checkListStatus,
    requiredOndcStatuses
  );

  const hasRazorpayError = isAnyErrorInServices(checkListStatus, requiredRazorpayStatuses);
  const hasBilldeskError = isAnyErrorInServices(checkListStatus, requiredBilldeskStatuses);
  const hasOndcError = isAnyErrorInServices(checkListStatus, requiredOndcStatuses);

  if (
    allRequiredServicesConfiguredForRazorpay ||
    allRequiredServicesConfiguredForBilldesk ||
    allRequiredServicesConfiguredForOndc
  ) {
    return "success";
  } else if (hasRazorpayError || hasBilldeskError || hasOndcError) {
    return "error";
  } else {
    return "";
  }
}

function getAMCsConfiguredStatus(checkListStatus: CheckListStatus) {
  const requiredStatuses: Partial<ConfigurationStatusService>[] = ["OMS_AMC", "PG_AMC"];

  const allRequiredServicesConfigured = isAllRequiredServicesConfigured(
    checkListStatus,
    requiredStatuses
  );
  const hasError = isAnyErrorInServices(checkListStatus, requiredStatuses);

  if (allRequiredServicesConfigured) {
    return "success";
  } else if (hasError) {
    return "error";
  } else {
    return "";
  }
}

function getKYCCheckConfiguredStatus(checkListStatus: CheckListStatus) {
  const requiredStatuses: Partial<ConfigurationStatusService>[] = ["KYC_GATEWAY"];

  const allRequiredServicesConfigured = isAllRequiredServicesConfigured(
    checkListStatus,
    requiredStatuses
  );
  const hasError = isAnyErrorInServices(checkListStatus, requiredStatuses);

  if (allRequiredServicesConfigured) {
    return "success";
  } else if (hasError) {
    return "error";
  } else {
    return "";
  }
}

function getNewKYCRequesetConfiguredStatus(checkListStatus: CheckListStatus) {
  const requiredStatuses: Partial<ConfigurationStatusService>[] = ["KYC_REQUEST", "FILES"];

  const allRequiredServicesConfigured = isAllRequiredServicesConfigured(
    checkListStatus,
    requiredStatuses
  );
  const hasError = isAnyErrorInServices(checkListStatus, requiredStatuses);

  if (allRequiredServicesConfigured) {
    return "success";
  } else if (hasError) {
    return "error";
  } else {
    return "";
  }
}

function getBankVerificationConfiguredStatus(checkListStatus: CheckListStatus) {
  const requiredStatuses: Partial<ConfigurationStatusService>[] = ["VERIFICATION", "FILES"];

  const allRequiredServicesConfigured = isAllRequiredServicesConfigured(
    checkListStatus,
    requiredStatuses
  );
  const hasError = isAnyErrorInServices(checkListStatus, requiredStatuses);

  if (allRequiredServicesConfigured) {
    return "success";
  } else if (hasError) {
    return "error ";
  } else {
    return "";
  }
}

function getBiToolsConfiguredStatus(checkListStatus: CheckListStatus) {
  const requiredStatuses: Partial<ConfigurationStatusService>[] = ["BI_TOOLS"];

  const allRequiredServicesConfigured = isAllRequiredServicesConfigured(
    checkListStatus,
    requiredStatuses
  );
  const hasError = isAnyErrorInServices(checkListStatus, requiredStatuses);

  if (allRequiredServicesConfigured) {
    return "success";
  } else if (hasError) {
    return "error ";
  } else {
    return "";
  }
}

function santizeobject(obj: any) {
  return obj ?? {};
}

function isServiceConfigurationObject(obj: any): obj is { status: string; message: string | null } {
  return (
    obj &&
    typeof obj.status === "string" &&
    (obj.message === null || typeof obj.message === "string")
  );
}

function calculateCheckListStatus(
  servicesStatus: Configurations.AutoSetupResponse | undefined,
  servicesBeingConfigured: ConfigurationStatusService[] | undefined,
  servicesConfigurationsResponse: Configurations.AutoSetupResponse | undefined
): CheckListStatus {
  const checkListStatus: CheckListStatus = {
    ONBOARDING: "NOT_CONFIGURED",
    FILES: "NOT_CONFIGURED",
    ACCOUNTS_MF: "NOT_CONFIGURED",
    BACKPRESSURE: "NOT_CONFIGURED",
    KYC_GATEWAY: "NOT_CONFIGURED",
    KYC_REQUEST: "NOT_CONFIGURED",
    VERIFICATION: "NOT_CONFIGURED",
    OMS_SEED: "NOT_CONFIGURED",
    OMS_RTA: "NOT_CONFIGURED",
    OMS_AMC: "NOT_CONFIGURED",
    PG_SEED: "NOT_CONFIGURED",
    PG_RAZORPAY_GATEWAY: "NOT_CONFIGURED",
    PG_BILLDESK_GATEWAY: "NOT_CONFIGURED",
    PG_AMC: "NOT_CONFIGURED",
    BI_TOOLS: "NOT_CONFIGURED",
    ORDER_SETTINGS: "NOT_CONFIGURED",
    PG_ONDC_GATEWAY: "NOT_CONFIGURED",
  };

  for (const [key, value] of Object.entries(santizeobject(servicesStatus))) {
    const service = key as ConfigurationStatusService;
    if (isServiceConfigurationObject(value)) {
      checkListStatus[service] = value?.status as CheckListStatuses;
    }
  }

  if (servicesBeingConfigured && servicesConfigurationsResponse) {
    for (const [key, value] of Object.entries(santizeobject(servicesConfigurationsResponse))) {
      const service = key as ConfigurationStatusService;
      if (isServiceConfigurationObject(value)) {
        const status = value?.status === "NOT_CONFIGURED" ? "ERROR" : "NOT_CONFIGURED";
        checkListStatus[service] = status as CheckListStatuses;
      }
    }
  }

  return checkListStatus;
}

const getKycCheckList = (kycCheck: FetchKycCheckConfigurationResponse) => {
  if (!isEmptyObject(kycCheck)) {
    let kycCheckList = "";
    if (kycCheck?.gateway === "amc-ipru") {
      kycCheckList = "IPRU";
    }

    if (kycCheck?.gateway === "kra-cams") {
      kycCheckList = "CAMS";
    }

    if (kycCheck?.gateway === "kra-cvl") {
      kycCheckList = "CVL";
    }

    return kycCheckList;
  } else {
    return "";
  }
};

const getKycRequestData = (data: KycRequestConfigurationResponse) => {
  if (!data) {
    return "";
  }

  const configurationType = getKycConfigurationType(data);

  switch (configurationType) {
    case "CYBRILLA_OPAT":
      return "FP Distributor (Via Cybrilla OPAT)";

    case "DISTRIBUTOR_AMC":
      return "FP Distributor AMC";

    case "OPAT_AMC":
      return "OPAT AMCs";

    default:
      return "";
  }
};

const getMfOrderList = (
  omsSettings: OmsTenantSettingsResponse,
  orderSettings: OrderConfigurationsResponse
) => {
  const distributor = omsSettings?.arn_code ?? omsSettings?.ria_code ?? "";
  let gateway = "";

  let rta = [];

  if (omsSettings?.rta_codes?.cams_broker_code) {
    rta.push("Cams");
  }

  if (omsSettings?.rta_codes?.karvy_broker_code) {
    rta.push("Kfintech");
  }

  if (orderSettings?.default_order_gateway === "ondc") {
    gateway = "ONDC";
  }

  if (orderSettings?.default_order_gateway === "ncdex") {
    gateway = "NCDEX";
  }

  const gateways = gateway ? `, Gateway: ${gateway}` : "";
  if (rta?.length) {
    return `${distributor}${gateways}, RTA: ${rta?.join(", ")} `;
  } else {
    return `${distributor}${gateways}`;
  }
};

const getVerficationData = (verficationData: Object | null, checkListStatus: CheckListStatus) => {
  if (getBankVerificationConfiguredStatus(checkListStatus) === "success") {
    return verficationData ? "Razorpay" : "Cybrilla default";
  } else {
    return "";
  }
};

const getPGData = (pg: GetPaymentsGetwayResponse) => {
  let pgSet = new Set();

  if (pg?.default_payment_gateway) {
    const defaultGateway =
      pg?.default_payment_gateway?.charAt(0)?.toUpperCase() + pg?.default_payment_gateway?.slice(1);
    if (defaultGateway === "Ondc") {
      pgSet.add("ONDC");
    } else {
      pgSet.add(defaultGateway);
    }

    if (pg?.providers) {
      pg?.providers?.forEach((item) => {
        const provider = item?.charAt(0)?.toUpperCase() + item?.slice(1);

        if (provider === "Ondc") {
          pgSet.add("ONDC");
        } else {
          pgSet.add(provider);
        }
      });
    }

    return Array.from(pgSet).join(", ");
  } else {
    return "";
  }
};

export default function ConfigurationStatus() {
  const { fpapi } = useFpapi();
  const userInfo = useUserInfo();
  const currentRoles = userInfo?.data.userInfo?.roles;
  const [servicesResponse, setAllServicesResponse] = useState<AutoSetupResponse | undefined>(
    undefined
  );
  const [error, setError] = useState("");
  const [batchFolioConfigError, setBatchFolioConfigError] = useState("");
  const [checkListStatus, setCheckListStatus] = useState<CheckListStatus>(() => {
    return calculateCheckListStatus(servicesResponse, undefined, undefined);
  });
  const [folioMigrationResponse, setFolioMigrationResponse] =
    useState<Extract<BatchConfigurationObject, { name: "FOLIO_MIGRATION" }>>();
  const [folioMigrationSwitch, setFolioMigrationSwitch] = useState(
    Boolean(folioMigrationResponse?.configuration?.enabled)
  );
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const [loading, setLoading] = useState(false);

  const fetchAllServiceStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fpapi?.fpDashboardClient?.configurations()?.getSandboxConfiguration();
      setAllServicesResponse(response);

      // fetch folio migration details if oms & backpressure enabled
      if (
        response?.ONBOARDING.status === "CONFIGURED" &&
        response?.BACKPRESSURE.status === "CONFIGURED"
      ) {
        const batchConfigurationResponse = await fpapi?.fpDashboardClient
          ?.batch()
          ?.fetchBatchConfiguration();
        const folioMigrationStatus = batchConfigurationResponse?.data?.find(
          (data): data is Extract<BatchConfigurationObject, { name: "FOLIO_MIGRATION" }> =>
            data.name === "FOLIO_MIGRATION"
        );
        setFolioMigrationResponse(folioMigrationStatus);
      }
    } catch (e: any) {
      reportErrorToSentry(e);

      setError("Error fetching data. Please try again");
      window.debug.error(e);
    } finally {
      setLoading(false);
    }
  }, [fpapi?.fpDashboardClient]);

  useEffect(() => {
    fetchAllServiceStatus();
  }, [fetchAllServiceStatus]);

  useEffect(() => {
    const latestCheckListStatus = calculateCheckListStatus(servicesResponse, undefined, undefined);
    setCheckListStatus(latestCheckListStatus);
  }, [servicesResponse]);

  useEffect(() => {
    setFolioMigrationSwitch(Boolean(folioMigrationResponse?.configuration?.enabled));
  }, [folioMigrationResponse]);

  const onFolioMigrationSwitchChange = () => {
    setIsModalOpen(true);
  };

  const onModalConfirmAction = async () => {
    setIsModalOpen(false);
    setLoading(true);
    const intendedActionViaConfirmAction = !folioMigrationSwitch;
    try {
      const folioMigrationConfigBody = {
        name: "FOLIO_MIGRATION" as const,
        configuration: {
          enabled: intendedActionViaConfirmAction,
          auto_migration_enabled: intendedActionViaConfirmAction,
        },
      };

      if (folioMigrationResponse === undefined) {
        const batchConfigurationCreateResponse = await fpapi?.fpDashboardClient
          ?.batch()
          ?.createBatchConfiguration(folioMigrationConfigBody);
        const folioMigrationConfigObj = batchConfigurationCreateResponse?.data?.find(
          (data): data is Extract<BatchConfigurationObject, { name: "FOLIO_MIGRATION" }> =>
            data.name === "FOLIO_MIGRATION"
        );
        setFolioMigrationResponse(folioMigrationConfigObj);
      } else {
        const batchConfigurationCreateResponse = await fpapi?.fpDashboardClient
          ?.batch()
          ?.updateBatchConfiguration(folioMigrationConfigBody);
        const folioMigrationConfigObj = batchConfigurationCreateResponse?.data?.find(
          (data): data is Extract<BatchConfigurationObject, { name: "FOLIO_MIGRATION" }> =>
            data.name === "FOLIO_MIGRATION"
        );
        setFolioMigrationResponse(folioMigrationConfigObj);
      }
    } catch (e: any) {
      reportErrorToSentry(e);
      setBatchFolioConfigError("Error updating data. Please try again.");
      window.debug.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <ConditionalRenderer check={loading}>
        <Loader variant="fullpage" />
      </ConditionalRenderer>
      <PageTitle title="Configuration Status" />
      <Description type="text" text="Review your app configurations here" />
      <PanelCard customClass="mb-4">
        <div className="space-around flex items-center">
          <div className="flex w-5/12 justify-center">
            <SandboxSvg />
          </div>
          <div>
            <Checklist>
              <ChecklistItem>
                <ChecklistStatus
                  status={getOMSConfiguredStatus(
                    checkListStatus,
                    servicesResponse?.ORDER_SETTINGS?.data
                  )}
                />
                <ChecklistTitle>MF orders</ChecklistTitle>
                <ChecklistDesc>
                  {getMfOrderList(
                    servicesResponse?.OMS_SEED?.data,
                    servicesResponse?.ORDER_SETTINGS?.data
                  )}
                </ChecklistDesc>
              </ChecklistItem>
              <ChecklistItem>
                <ChecklistStatus status={getPGConfiguredStatus(checkListStatus)} />
                <ChecklistTitle>Payments</ChecklistTitle>
                <ChecklistDesc>{getPGData(servicesResponse?.PG_SEED?.data)}</ChecklistDesc>
              </ChecklistItem>
              <ChecklistItem>
                <ChecklistStatus status={getAMCsConfiguredStatus(checkListStatus)} />
                <ChecklistTitle>AMCs</ChecklistTitle>
                <ChecklistDesc>
                  {servicesResponse?.OMS_AMC?.data?.data?.length
                    ? `${servicesResponse?.OMS_AMC?.data?.data?.length} AMCs configured`
                    : ""}
                </ChecklistDesc>
              </ChecklistItem>
              <ChecklistItem>
                <ChecklistStatus status={getKYCCheckConfiguredStatus(checkListStatus)} />
                <ChecklistTitle>KYC Check</ChecklistTitle>
                <ChecklistDesc>
                  {getKycCheckList(servicesResponse?.KYC_GATEWAY?.data) ?? ""}
                </ChecklistDesc>
              </ChecklistItem>
              <ChecklistItem>
                <ChecklistStatus status={getNewKYCRequesetConfiguredStatus(checkListStatus)} />
                <ChecklistTitle>New KYC request</ChecklistTitle>
                <ChecklistDesc>
                  {getKycRequestData(servicesResponse?.KYC_REQUEST?.data)}
                </ChecklistDesc>
              </ChecklistItem>
              <ChecklistItem>
                <ChecklistStatus status={getBankVerificationConfiguredStatus(checkListStatus)} />
                <ChecklistTitle>Bank account verification</ChecklistTitle>
                <ChecklistDesc>
                  {getVerficationData(servicesResponse?.VERIFICATION?.data, checkListStatus)}
                </ChecklistDesc>
              </ChecklistItem>
              <ChecklistItem>
                <ChecklistStatus status={getBiToolsConfiguredStatus(checkListStatus)} />
                <ChecklistTitle>BI Tools</ChecklistTitle>
              </ChecklistItem>
            </Checklist>

            <ConditionalRenderer check={Boolean(error)}>
              <div className="flex">
                <Button
                  variant="primary"
                  fullwidth
                  customClass="mr-2"
                  size="sm"
                  onClick={() => window.location.reload()}
                >
                  Retry
                </Button>
              </div>

              <Message type="error" variant="field" title={error} />
            </ConditionalRenderer>
          </div>
        </div>
      </PanelCard>

      <ConditionalRenderer
        check={
          getOMSConfiguredStatus(checkListStatus, servicesResponse?.ORDER_SETTINGS?.data) ===
            "success" && isAuthorisedRole(FOLIO_MIGRATION_SWITCH_AUTHORISED_ROLES, currentRoles)
        }
      >
        <PageTitle title="Advanced features" />
        <Panel>
          <div className="flex justify-between">
            <div>
              <PageTitle semiBold={true} title="Folio migration" />
              <Description
                type="text"
                text="Seamlessly migrate your folios by just uploading the Folio Feed files"
              />
              <ConditionalRenderer check={Boolean(batchFolioConfigError)}>
                <Message type="error" variant="field" title={batchFolioConfigError} />
              </ConditionalRenderer>
            </div>
            <div className="flex items-center">
              <Label
                message={
                  Boolean(folioMigrationResponse?.configuration?.enabled) ? "Enabled" : "Disabled"
                }
                customClass="mr-3"
              />

              <ToggleSwitch
                enabled={folioMigrationSwitch}
                onChange={onFolioMigrationSwitchChange}
              />
            </div>
          </div>
        </Panel>

        <Modal
          variant="curvedEdge"
          title="Confirm your selection"
          isOpen={isModalOpen}
          toggleModal={() => {
            setIsModalOpen(false);
          }}
          fullWidth
        >
          <ConditionalRenderer check={folioMigrationSwitch}>
            <Text>Are you sure you want to disable folio migration feature?</Text>
            <div className="full-width mt-3 flex justify-end">
              <Button
                customClass="mr-3"
                variant="secondary"
                size="sm"
                onClick={() => setIsModalOpen(false)}
              >
                Cancel
              </Button>
              <Button size="sm" onClick={onModalConfirmAction}>
                Confirm
              </Button>
            </div>
          </ConditionalRenderer>
          <ConditionalRenderer check={!folioMigrationSwitch}>
            <Text>
              After enabling, folios will be auto-migrated once the feed files are uploaded.
            </Text>
            <div className="full-width mt-3 flex justify-end">
              <Button
                customClass="mr-3"
                variant="secondary"
                size="sm"
                onClick={() => setIsModalOpen(false)}
              >
                Cancel
              </Button>
              <Button size="sm" onClick={onModalConfirmAction}>
                Confirm
              </Button>
            </div>
          </ConditionalRenderer>
        </Modal>
      </ConditionalRenderer>
    </div>
  );
}
